import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { SettingsHostProps, SettingsItem } from 'shared/settings';
import { usePermissions } from '@/providers/Permissions.provider';
import usePreFundingEnabled from '@/hooks/usePreFundingEnabled';
import useSettingsLockState from './useSettingsLockState';

// The one place client-dashboard maps its own feature-flag/permission model onto the shared
// Settings shell's props. partner-dashboard writes its own equivalent in ID-13820, with its own
// (much shorter) card list. Card/side-menu copy stays here — the shell has no portal feature
// catalogue of its own (see shared/users' adapter-hook precedent). `children` (the host's own
// inner-route <Switch>) isn't this hook's concern — SettingsPage passes it alongside these props.
export default function useSettingsHostProps(): Omit<SettingsHostProps, 'children'> {
  const history = useHistory();
  const { permissions } = usePermissions();
  const preFundingEnabled = usePreFundingEnabled();
  const { brandingLocked, elinxLocked, domainSetupLocked } = useSettingsLockState();

  const { t } = useTranslation('translations', { keyPrefix: 'Settings' });
  const { t: tCards } = useTranslation('translations', { keyPrefix: 'Settings.Cards' });

  const items: SettingsItem[] = [
    {
      id: 'webhooks',
      icon: 'webhooks',
      path: '/settings/webhooks',
      title: tCards('Webhooks.title', { defaultValue: 'Webhooks' }),
      subtitle: tCards('Webhooks.subtitle', { defaultValue: 'Get real-time event notifications' }),
      description: tCards('Webhooks.description', {
        defaultValue: 'Add your endpoint URLs and choose which transaction and account events to receive',
      }),
      buttonLabel: tCards('Webhooks.button', { defaultValue: 'Manage Webhooks' }),
    },
    {
      id: 'branding',
      icon: 'brandSettings',
      path: '/settings/brand-settings',
      title: tCards('Branding.title', { defaultValue: 'Branding Settings' }),
      subtitle: tCards('Branding.subtitle', { defaultValue: 'Show your brand to your customers' }),
      description: tCards('Branding.description', {
        defaultValue: 'Upload your logo and set your colours across the emails, forms and pages your customers see',
      }),
      buttonLabel: tCards('Branding.button', { defaultValue: 'Manage Branding' }),
      isLocked: brandingLocked,
      lockedDescription: tCards('Branding.lockedDescription', {
        defaultValue: 'Upload your logo and set your colours across the emails, forms and pages your customers see.',
      }),
    },
    {
      id: 'authorizedIps',
      icon: 'ipAddress',
      path: '/settings/authorized-ips',
      title: tCards('AuthorizedIps.title', { defaultValue: 'Authorized IPs' }),
      subtitle: tCards('AuthorizedIps.subtitle', { defaultValue: 'Restrict API access by IP' }),
      description: tCards('AuthorizedIps.description', {
        defaultValue: 'Only the IP addresses you list here can make API requests with your account credentials',
      }),
      buttonLabel: tCards('AuthorizedIps.button', { defaultValue: 'Manage IPs' }),
    },
    ...(permissions.ManageEmailDomains
      ? [
          {
            id: 'emailDomain',
            icon: 'domainSetup',
            path: '/settings/domain-setup',
            title: tCards('EmailDomain.title', { defaultValue: 'Email Domain' }),
            subtitle: tCards('EmailDomain.subtitle', { defaultValue: 'Send emails that land in the inbox' }),
            description: tCards('EmailDomain.description', {
              defaultValue: 'Verify your domain with two DNS records to authenticate your emails and improve delivery',
            }),
            buttonLabel: tCards('EmailDomain.button', { defaultValue: 'Verify Domain' }),
            isLocked: domainSetupLocked,
            lockedDescription: tCards('EmailDomain.lockedDescription', {
              defaultValue: 'Verify your domain with two DNS records to authenticate your emails and improve delivery.',
            }),
          } satisfies SettingsItem,
        ]
      : []),
    {
      id: 'elinx',
      icon: 'elinx',
      path: '/settings/elinx',
      title: tCards('Elinx.title', { defaultValue: 'eLinx Settings' }),
      subtitle: tCards('Elinx.subtitle', { defaultValue: 'Set defaults for eLinx requests' }),
      description: tCards('Elinx.description', {
        defaultValue: 'Control request expiry, decline options, notifications, reminders and delivery channel',
      }),
      buttonLabel: tCards('Elinx.button', { defaultValue: 'Manage eLinx Settings' }),
      isLocked: elinxLocked,
      lockedDescription: tCards('Elinx.lockedDescription', {
        defaultValue: 'Control request expiry, decline options, notifications, reminders and delivery channel.',
      }),
    },
    ...(permissions.ViewSettings && preFundingEnabled
      ? [
          {
            id: 'preFunding',
            icon: 'preFunding',
            path: '/settings/pre-funding',
            title: tCards('PreFunding.title', { defaultValue: 'Pre-funding Settings' }),
            subtitle: tCards('PreFunding.subtitle', { defaultValue: 'Choose how payments are funded' }),
            description: tCards('PreFunding.description', {
              defaultValue: 'Choose whether payments draw from your VoPay balance or your bank account',
            }),
            buttonLabel: tCards('PreFunding.button', { defaultValue: 'Manage Pre-funding' }),
          } satisfies SettingsItem,
        ]
      : []),
  ];

  return {
    title: t('pageTitle', { defaultValue: 'Settings & Configuration' }),
    basePath: '/settings',
    items,
    hostActions: {
      onRequestAccess: (item) =>
        history.push({
          pathname: '/create-tickets',
          state: { ticketSubject: `Enquiry about ${item.title} feature` },
        }),
    },
  };
}
